import { useState, useEffect, useCallback } from 'react';
import { X, Search, UserPlus, UserMinus, Users, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { groupsAPI, userAPI } from '../../../../../services';
import { extractArray } from '../utils';

export default function UserPanel({ groupId, groupName, onSuccess, onClose }) {
  const [currentUsers, setCurrentUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState('members');
  const [selected, setSelected] = useState(new Set());

  const mapUser = (u) => ({
    // Priority: id (from not-in-group endpoint) → _id (from in-group raw docs) →
    // userId (legacy) → username → email (fallback)
    id: u.id || u.accountId || u._id || u.userId || u.username || u.email || `u-${Math.random()}`,
    email: u.email || u.userId || 'N/A',
    name: u.name || u.username || u.email || u.userId || 'Unknown',
    status: u.status || (u.isBlocked ? 'inactive' : 'active'),
    isVerified: u.isVerified,
    role: u.role,
  });

  const showMsg = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const fetchGroupUsers = useCallback(async () => {
    try {
      const result = await groupsAPI.getUsersInGroup(groupId);
      setCurrentUsers(extractArray(result).map(mapUser));
    } catch (err) {
      setError(err.message || 'Failed to load group members');
    }
  }, [groupId]);

  const fetchAvailable = useCallback(async () => {
    try {
      const result = await userAPI.getUsersNotInGroup(groupId);
      const all = extractArray(result).map(mapUser);
      const groupIds = new Set(currentUsers.map((u) => u.id));
      setAvailableUsers(all.filter((u) => !groupIds.has(u.id)));
    } catch {
      setAvailableUsers([]);
    }
  }, [groupId, currentUsers]);

  useEffect(() => {
    (async () => {
      setFetching(true);
      await fetchGroupUsers();
      setFetching(false);
    })();
  }, [fetchGroupUsers]);

  useEffect(() => {
    fetchAvailable();
  }, [fetchAvailable]);

  // Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [loading, onClose]);

  const addUser = async (userId) => {
    try {
      setLoading(true);
      setError('');
      await groupsAPI.addUserToGroup(groupId, userId);
      await fetchGroupUsers();
      showMsg('User added successfully');
    } catch (err) {
      setError(err.message || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const removeUser = async (userId) => {
    if (!window.confirm('Remove this user from the group?')) return;
    try {
      setLoading(true);
      setError('');
      await groupsAPI.removeUserFromGroup(groupId, userId);
      await fetchGroupUsers();
      showMsg('User removed successfully');
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    const ids = [...selected];
    if (!window.confirm(`${action === 'remove' ? 'Remove' : 'Add'} ${ids.length} user(s)?`)) return;
    try {
      setLoading(true);
      setError('');
      if (action === 'remove') {
        for (const id of ids) await groupsAPI.removeUserFromGroup(groupId, id);
      } else {
        for (const id of ids) await groupsAPI.addUserToGroup(groupId, id);
      }
      setSelected(new Set());
      await fetchGroupUsers();
      showMsg(`${ids.length} user(s) ${action === 'remove' ? 'removed' : 'added'}`);
    } catch (err) {
      setError(err.message || 'Bulk action failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredCurrent = currentUsers.filter((u) => {
    const s = searchTerm.toLowerCase();
    return u.id.toLowerCase().includes(s) || u.name.toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
  });

  const filteredAvailable = availableUsers.filter((u) => {
    const s = searchTerm.toLowerCase();
    return u.id.toLowerCase().includes(s) || u.name.toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
  });

  if (fetching) {
    return (
      <div className="gm-panel-overlay">
        <div className="gm-panel-backdrop" />
        <div className="gm-panel" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="gm-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="gm-panel-overlay">
      {/* Backdrop */}
      <div className="gm-panel-backdrop" onClick={() => !loading && onClose()} />

      {/* Panel */}
      <div className="gm-panel animate-slide-in-right">
        {/* Header */}
        <div className="gm-panel-header">
          <div>
            <h2 className="gm-panel-title">
              <Users size={20} color="var(--admin-primary)" />
              Manage Users
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-secondary)', marginTop: 2 }}>
              Group: <span style={{ fontWeight: 500, color: 'var(--admin-text-primary)' }}>{groupName}</span>
            </p>
          </div>
          <button onClick={onClose} disabled={loading} className="gm-panel-close">
            <X size={20} />
          </button>
        </div>

        {/* Stats */}
        <div className="gm-panel-stats">
          <div className="gm-panel-stat">
            <p className="gm-panel-stat-value">{currentUsers.length}</p>
            <p className="gm-panel-stat-label">Members</p>
          </div>
          <div className="gm-panel-stat">
            <p className="gm-panel-stat-value">{availableUsers.length}</p>
            <p className="gm-panel-stat-label">Available</p>
          </div>
          <div className="gm-panel-stat">
            <p className="gm-panel-stat-value gm-panel-stat-value-teal">{selected.size}</p>
            <p className="gm-panel-stat-label">Selected</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="gm-tabs">
          <button
            onClick={() => { setActiveTab('members'); setSelected(new Set()); }}
            className={`gm-tab-btn ${activeTab === 'members' ? 'gm-tab-btn-active' : ''}`}
          >
            Members ({currentUsers.length})
          </button>
          <button
            onClick={() => { setActiveTab('available'); setSelected(new Set()); }}
            className={`gm-tab-btn ${activeTab === 'available' ? 'gm-tab-btn-active' : ''}`}
          >
            Add Users ({availableUsers.length})
          </button>
        </div>

        {/* Search */}
        <div className="gm-panel-search">
          <div className="gm-search-input-wrap" style={{ maxWidth: '100%' }}>
            <Search className="gm-search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID, name, or email..."
              className="gm-search-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="gm-search-clear">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="gm-bulk-bar">
            <span className="gm-bulk-bar-text">{selected.size} selected</span>
            <div className="gm-bulk-bar-actions">
              {activeTab === 'members' ? (
                <button onClick={() => bulkAction('remove')} disabled={loading} className="gm-btn-xs gm-btn-xs-danger">
                  <UserMinus size={12} /> Remove
                </button>
              ) : (
                <button onClick={() => bulkAction('add')} disabled={loading} className="gm-btn-xs gm-btn-xs-teal">
                  <UserPlus size={12} /> Add
                </button>
              )}
              <button onClick={() => setSelected(new Set())} className="gm-btn-xs-clear">Clear</button>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="gm-panel-msg gm-panel-msg-error">
            <AlertCircle size={16} style={{ flexShrink: 0 }} /> {error}
          </div>
        )}
        {successMsg && (
          <div className="gm-panel-msg gm-panel-msg-success">
            <CheckCircle2 size={16} style={{ flexShrink: 0 }} /> {successMsg}
          </div>
        )}

        {/* User list */}
        <div className="gm-list">
          {(activeTab === 'members' ? filteredCurrent : filteredAvailable).length === 0 ? (
            <div className="gm-list-empty">
              <Users className="gm-list-empty-icon" />
              <p className="gm-list-empty-text">
                {searchTerm ? 'No users match your search.' : activeTab === 'members' ? 'No members in this group.' : 'No available users.'}
              </p>
            </div>
          ) : (
            <div>
              {(activeTab === 'members' ? filteredCurrent : filteredAvailable).map((user) => (
                <div
                  key={user.id}
                  className={`gm-list-item ${selected.has(user.id) ? 'gm-list-item-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(user.id)}
                    onChange={() => toggleSelect(user.id)}
                    className="gm-list-checkbox"
                  />
                  <div className="gm-list-avatar">
                    {user.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="gm-list-item-info">
                    <p className="gm-list-item-name">{user.name}</p>
                    <p className="gm-list-item-id">{user.id}</p>
                  </div>
                  <span className={`gm-badge ${user.status === 'active' ? 'gm-badge-active' : 'gm-badge-inactive'}`}>
                    <span className={`gm-badge-dot ${user.status === 'active' ? 'gm-badge-dot-active' : 'gm-badge-dot-gray'}`} />
                    {user.status}
                  </span>
                  {activeTab === 'members' ? (
                    <button onClick={() => removeUser(user.id)} disabled={loading}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, background: 'none', border: 'none',
                        borderRadius: 6, cursor: 'pointer', color: 'var(--admin-text-light)',
                      }}
                      title="Remove user"
                      onMouseOver={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--admin-text-light)'; }}
                    >
                      <UserMinus size={16} />
                    </button>
                  ) : (
                    <button onClick={() => addUser(user.id)} disabled={loading}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, background: 'none', border: 'none',
                        borderRadius: 6, cursor: 'pointer', color: 'var(--admin-text-light)',
                      }}
                      title="Add user"
                      onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(0,184,148,0.08)'; e.currentTarget.style.color = 'var(--admin-primary)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--admin-text-light)'; }}
                    >
                      <UserPlus size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Manual Add */}
        <div className="gm-panel-footer">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const input = e.target.elements.userId;
              if (input.value.trim()) {
                addUser(input.value.trim());
                input.value = '';
              }
            }}
            className="gm-panel-footer-form"
          >
            <input
              name="userId"
              type="text"
              placeholder="Enter user ID to add directly..."
              className="gm-input"
              style={{ flex: 1 }}
            />
            <button
              type="submit"
              disabled={loading}
              className="gm-btn-primary"
            >
              <UserPlus size={16} />
              Add
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
